/**
 * ENG-9206 — "Waiting for approval": record the intent to install an
 * integration whose tenant blocks user consent, instead of the install vanishing.
 *
 * ## What actually happens today
 *
 * When a customer connects an integration whose tenant restricts user consent
 * (Microsoft 365 is the common case), the OAuth flow does not fail — it
 * *terminates*. Microsoft shows "Approval required", the user submits a request
 * to their tenant admin, and no authorization code is ever issued. Composio
 * stores nothing. From our side the install simply does not exist:
 *
 * ```
 * razorclaw-ea / acquire-intelligence, composio/outlook, probed 2026-08-20T00:09:47Z
 *   verdict:  down
 *   message:  "No connected account recorded — reconnect required"
 * ```
 *
 * The user's model is "I connected it and asked my admin." Ours is "nothing
 * happened." Those disagree for as long as the admin takes, and that gap is
 * where the support ticket lives.
 *
 * ## Recorded intent, NOT a detected state
 *
 * Microsoft's admin-consent path never calls back. "Waiting on their admin" is
 * indistinguishable from "closed the tab", "hit cancel" and "the flow errored" —
 * all four produce the same observable, which is no connected account. So this
 * state is written when Connect is INITIATED and resolved when a connected
 * account actually appears. It is a lifecycle record, and it must never be
 * inferred by a probe.
 *
 * ## Why it cannot be allowed to look healthy
 *
 * `integration-health.ts` exists because this class of bug has been fixed and
 * reintroduced six times (ENG-6139, ENG-6157, ENG-6328, ENG-7214, ENG-7405,
 * ENG-8226). A status that reads as "sort of added" is exactly the shape that
 * keeps recreating it, so:
 *
 *   - `pending_approval` derives to **`down`**, never `ok`, never green.
 *   - It is outside `('active','configured')`, which is the set both
 *     `POST /host/agent-integrations` and `effective-integrations.ts` provision
 *     from — so it cannot bind tools and cannot count toward `effective_count`
 *     **by construction** rather than by a filter somebody has to remember.
 *
 * `down` rather than `unverified` is deliberate. `unverified` means "we could not
 * check". Here we are not guessing: there is definitively no working connection,
 * and the honest verdict for the health axis is that it does not work. The fact
 * that this is an *expected, benign* reason for it not to work belongs on the
 * lifecycle axis — which is the whole point of keeping the two separate.
 *
 * ## The expiry is a property of the state, not a cron
 *
 * An install state with no terminal transition is a row that never closes. That
 * failure mode is live elsewhere right now: as of this morning 6 of 10 open
 * `agent_paused` criticals describe agents that are running fine, because the
 * close hangs off one caller instead of off the state change.
 *
 * So the expiry here is DERIVED at read time from when the state was entered. No
 * sweep has to run for a stale "Waiting for approval" to stop presenting itself
 * as a live wait — if the cron never fires, the derived state still expires on
 * schedule for every reader. A cron may later rewrite the row for tidiness, but
 * nothing user-visible depends on it doing so.
 */

/** The install-lifecycle state written when a consent-gated connect is started. */
export const PENDING_APPROVAL_STATUS = 'pending_approval' as const;

/**
 * How long a "Waiting for approval" record stays a live wait.
 *
 * Seven days: long enough that a slow IT department does not invalidate a
 * genuine request, short enough that an abandoned one stops occupying the UI
 * within a working week. First guess per the ticket, and deliberately a single
 * named constant so changing it is one reviewable edit.
 */
export const PENDING_APPROVAL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * How long after a connect is initiated we say nothing about admin approval.
 *
 * THE PROBLEM THIS SOLVES. The row is written when Connect is INITIATED,
 * because that is the only moment we have (Microsoft never calls back). But the
 * overwhelmingly common outcome of pressing Connect is that the user completes
 * OAuth in about twenty seconds and the callback flips the row to `active`. If
 * the record presented itself as "your admin has been asked to approve this"
 * from the instant it was written, then for those twenty seconds — and for
 * every non-consent-gated tenant, which is most of them — we would be asserting
 * something that never happened. That is the same "surface an assumed state
 * rather than a checked one" defect as ENG-9216, just pointed at a different
 * screen.
 *
 * So the WRITE is unconditional at initiation (recorded intent, per the ticket)
 * and the CLAIM is delayed. Inside the grace the honest statement is "this is
 * still finishing"; only once a connect has been open far longer than a
 * successful one ever takes does the admin-consent explanation become the
 * likeliest one worth showing.
 *
 * Five minutes: an order of magnitude beyond a normal OAuth round-trip
 * (~20s) including a password prompt, an MFA push and a slow redirect, while
 * still resolving well inside the session in which the user pressed Connect.
 */
export const PENDING_APPROVAL_GRACE_MS = 5 * 60 * 1000;

/**
 * What a `pending_approval` record means RIGHT NOW.
 *
 *  - `connecting` — inside the grace window. The flow was started moments ago
 *    and is most likely simply still in progress. Say nothing about admins.
 *  - `waiting` — past the grace, inside the TTL. A connect this old did not
 *    complete normally; tenant-admin consent is the explanation worth showing,
 *    and the admin may still act.
 *  - `expired` — past the TTL. Terminal for presentation purposes: the user is
 *    told to start again rather than left watching a request nobody will answer.
 */
export type PendingApprovalPhase = 'connecting' | 'waiting' | 'expired';

export interface PendingApprovalPhaseInputs {
  /** When the state was entered (ISO). Missing/unparseable is treated as expired. */
  enteredAt?: string | null;
  /** Evaluation instant, injected so this is testable and deterministic. */
  now: Date;
  /** Override for tests; defaults to {@link PENDING_APPROVAL_TTL_MS}. */
  ttlMs?: number;
  /** Override for tests; defaults to {@link PENDING_APPROVAL_GRACE_MS}. */
  graceMs?: number;
}

/**
 * Resolve the phase of a pending-approval record.
 *
 * Fails towards `expired` when the entry time is missing or unparseable. That is
 * the safe direction: an unbounded "waiting" is the row-that-never-closes this
 * design exists to avoid, whereas an over-eager `expired` tells the user to
 * press Connect again — which is the correct action in every case anyway,
 * because admin approval alone does not complete the connection.
 */
export function pendingApprovalPhase(inputs: PendingApprovalPhaseInputs): PendingApprovalPhase {
  const ttlMs = inputs.ttlMs ?? PENDING_APPROVAL_TTL_MS;
  const graceMs = inputs.graceMs ?? PENDING_APPROVAL_GRACE_MS;
  if (typeof inputs.enteredAt !== 'string' || inputs.enteredAt.length === 0) return 'expired';
  const enteredMs = Date.parse(inputs.enteredAt);
  if (!Number.isFinite(enteredMs)) return 'expired';
  const age = inputs.now.getTime() - enteredMs;
  // A negative age means the row claims to have been entered in the future —
  // clock skew between the writer and the reader. Treat it as `connecting`
  // rather than letting `age < graceMs` fall through to the same answer by
  // accident: it is the same "we do not know yet, say nothing about admins"
  // situation, and it must not be reachable by `expired`.
  if (age < graceMs) return 'connecting';
  return age < ttlMs ? 'waiting' : 'expired';
}

/**
 * The user-facing copy, and the second sentence is the load-bearing one.
 *
 * Admin approval does NOT resume the flow — consent is a precondition, not a
 * resumption, so there is no pending authorization to revive. The user must run
 * Connect again. Everyone misses this step, which is why it is in the string
 * rather than left to a docs page: without it the experience reads as "my admin
 * approved it and it is still broken".
 */
export function pendingApprovalMessage(phase: PendingApprovalPhase): string {
  switch (phase) {
    // Deliberately says nothing about admins or approval. Inside the grace we
    // do not know that consent is why this has not completed, and the most
    // likely answer is that nothing is wrong at all — see
    // PENDING_APPROVAL_GRACE_MS.
    case 'connecting':
      return 'Finishing the connection. If this is still here in a few minutes, your tenant may require an admin to approve it first.';
    case 'waiting':
      return 'Your admin has been asked to approve this. Once they have, click Connect again — approval alone does not finish the connection.';
    case 'expired':
      return 'This approval request has expired. Click Connect again to restart it — approval alone does not finish the connection.';
  }
}

/**
 * Toolkits whose tenant can block user consent, so a connect can terminate at
 * an "Approval required" screen with no code issued and no callback.
 *
 * This is the Microsoft 365 / Entra ID family. Microsoft is the only provider
 * in the catalog today whose admin-consent flow behaves this way: the user is
 * shown a request form, the request goes to the tenant admin out of band, and
 * nothing is ever sent back to the relying party. Google Workspace's equivalent
 * blocks BEFORE the user reaches us, and every other managed toolkit either
 * completes or errors.
 *
 * WHY AN EXPLICIT LIST RATHER THAN A PATTERN. A false positive here is not
 * cosmetic: a row that should have gone straight to `active` instead spends the
 * grace window outside the provisioned set, and after five minutes starts
 * telling the user to chase an admin who was never asked. Matching
 * /microsoft|office/ would eventually catch something unrelated. Three ids is a
 * short list and adding to it is a one-line, reviewable change — which is the
 * right cost for a decision that changes what a customer is told.
 *
 * Extending it: add the toolkit id when a Microsoft-family toolkit is seeded
 * (SharePoint and Outlook Calendar are the obvious next two). ENG-8755 /
 * ADR-0060 — Microsoft 365 onto the native lane with our own Entra app,
 * tenant-admin-consented once — removes the need for this list entirely, and
 * this ticket is explicitly the interim UX until that lands.
 */
export const TENANT_ADMIN_CONSENT_TOOLKIT_IDS: ReadonlySet<string> = new Set([
  'composio/outlook',
  'composio/onedrive',
  'pipedream/microsoft-teams',
]);

/**
 * Can a connect against this toolkit terminate at a tenant-admin-consent screen?
 *
 * Only these toolkits get a `pending_approval` record written when Connect is
 * initiated. For everything else an incomplete connect leaves the row exactly
 * as it is today, because for those toolkits "no connected account" genuinely
 * does mean the user abandoned or the flow errored, and inventing a
 * waiting-on-your-admin story for that would be the same fabrication in the
 * other direction.
 */
export function canRequireTenantAdminConsent(toolkitId: string | null | undefined): boolean {
  return typeof toolkitId === 'string' && TENANT_ADMIN_CONSENT_TOOLKIT_IDS.has(toolkitId);
}
